import styles from "./budget.module.css"

import React from "react"
import merge from "lodash/merge"
import cx from "classnames"
import * as recharts from "recharts"
import * as util from "activities/util"
import * as fonts from "shared/fonts"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import * as pie from "activities/components/charts/pie"
import assign from "object-assign"
import AccessibilityTable from "activities/components/shared/accessibility-table"

// TODO: extract the shared PieChart elements
export class BudgetPieChart extends React.Component {
  render() {
    const {colors = pie.pieDefaults.colors, dataKey = pie.pieDefaults.data, label = pie.pieDefaults.label, overspendColor = "#991125", overspendTitle = "Overspend"} = this.props
    let {data = pie.pieDefaults.data, income} = this.props

    const total = data.map((e) => e[dataKey]).reduce((sum, current) => sum + current, 0)
    income = income || total
    const overspend = total - income
    const overspendAngle = 360 * (overspend / total)

    const isEmpty = total === 0
    if (isEmpty) {
      data = [{}]
      data[0][dataKey] = 100
    }

    const didOverspend = overspend > 0
    return (
      <recharts.ResponsiveContainer width={"100%"} height={"100%"}>
        <recharts.PieChart>
          {!isEmpty && <recharts.Tooltip cursor={false} content={<pie.PieChartTooltip total={total} />} wrapperStyle={pie.pieTooltipStyles} />}
          <recharts.Pie data={data} dataKey={dataKey} innerRadius={75} startAngle={90} endAngle={-270} rootTabIndex={-1}>
            <recharts.Label className={cx(styles["label"], fonts.getFontClass())} value={label} fill="#5C5761" position="center" />
            {isEmpty || data.map((entry, index) => <recharts.Cell key={`cell-${index}`} fill={colors[index]} strokeWidth={1} />)}
            {!isEmpty || <recharts.Cell fill="#DCDEE0" strokeWidth={1} />}
          </recharts.Pie>
          {didOverspend && (
            <recharts.Pie data={[{overspend, name: overspendTitle}]} dataKey="overspend" innerRadius="70%" startAngle={90} endAngle={90 + overspendAngle} rootTabIndex={-1}>
              <recharts.Cell fill={overspendColor} strokeWidth={1} />
            </recharts.Pie>
          )}
        </recharts.PieChart>
      </recharts.ResponsiveContainer>
    )
  }
}

const mergeExpenses = (monthly, yearly) => {
  const copy = yearly.map((e) => merge({}, e, {cost: e.cost / 12}))
  return monthly.concat(copy)
}

const sumCosts = (expenses) => {
  return expenses.map((e) => e.cost).reduce((total, next) => total + next, 0)
}

const Selections = (props) => {
  const {
    id,
    monthlyIncome,
    incomeIntegerLimit,
    monthly,
    monthlyOptions,
    monthlyColors,
    monthlyIntegerLimit,
    yearly,
    yearlyOptions,
    yearlyColors,
    yearlyIntegerLimit,
    updateState,
    setState,
    resetKey,
    theme
  } = props
  return (
    <div className={styles["selections"]}>
      <inputs.CalcInput
        id={id}
        className={styles["input"]}
        title="Monthly After-Tax Income"
        initialValue={monthlyIncome}
        onChange={updateState("monthlyIncome", util.fromString)}
        resetKey={resetKey}
        integerLimit={incomeIntegerLimit}
      />
      <inputs.ExpenseForm
        className={styles["entry"]}
        entryClassName={styles["dropdown"]}
        arrowClassName={styles["dropdown-arrow"]}
        title="Monthly Expenses"
        initialValue={monthly}
        integerLimit={monthlyIntegerLimit}
        onRowAdd={util.addRow(setState, "monthly")}
        onRowRemove={util.removeRow(setState, "monthly")}
        onRowChange={util.updateRow(setState, "monthly")}
        options={monthlyOptions}
        colors={monthlyColors}
        min={4}
        max={monthlyOptions.length}
        resetKey={resetKey}
        theme={theme}
        dataTestId={"monthly-expenses"}
      />
      <inputs.ExpenseForm
        className={styles["entry"]}
        entryClassName={styles["dropdown"]}
        arrowClassName={styles["dropdown-arrow"]}
        title="Yearly Expenses"
        initialValue={yearly}
        integerLimit={yearlyIntegerLimit}
        onRowAdd={util.addRow(setState, "yearly")}
        onRowRemove={util.removeRow(setState, "yearly")}
        onRowChange={util.updateRow(setState, "yearly")}
        options={yearlyOptions}
        colors={yearlyColors}
        min={2}
        max={yearlyOptions.length}
        resetKey={resetKey}
        theme={theme}
        dataTestId={"yearly-expenses"}
      />
    </div>
  )
}

const calcThings = ({monthlyIncome, monthly, yearly}) => {
  const mergedExpenses = mergeExpenses(monthly, yearly).filter((e) => e.cost && e.name)
  mergedExpenses.sort((a, b) => a.colorId - b.colorId)
  const monthlyNetIncome = monthlyIncome - sumCosts(mergedExpenses)
  return {
    monthlyNetIncome: monthlyNetIncome,
    yearlyNetIncome: monthlyNetIncome * 12,
    mergedExpenses: mergedExpenses
  }
}

export default shared.ActivityFactory(
  ({getProps, getState, updateState, setState}) => {
    const state = getState()
    const {monthly, yearly, colorClaims, resetKey, monthlyIncome} = state
    const {
      colors,
      underspendColor,
      underspendTitle,
      overspendColor,
      overspendTitle,
      monthlyOptions,
      yearlyOptions,
      incomeIntegerLimit,
      monthlyIntegerLimit,
      yearlyIntegerLimit,
      theme,
      id
    } = getProps()
    const monthlyColors = util.mapColors(colorClaims, colors, "monthly")
    const yearlyColors = util.mapColors(colorClaims, colors, "yearly")
    const {monthlyNetIncome, yearlyNetIncome, mergedExpenses} = calcThings(state)
    const pieData = monthlyNetIncome > 0 ? mergedExpenses.concat([{cost: monthlyNetIncome, name: underspendTitle}]) : mergedExpenses
    const pieColors = mergedExpenses.map((e) => colors[e.colorId]).concat([underspendColor])

    const accessibilityTableHeadings = [
      {
        name: "Category",
        key: "name"
      },
      {
        name: "Cost",
        key: "cost"
      },
      {
        name: "Percent",
        key: "percent"
      }
    ]

    const accessibilityTableData = pieData.map((entry) => {
      const percent = (entry.cost / monthlyIncome) * 100
      return {name: entry.name, cost: util.formatDollarsNoCents(entry.cost), percent: percent.toFixed(2)}
    })

    const accessibilityTableLabel = "Budget categories and percent of total money spent in each category."

    return (
      <div>
        <shared.Content className={styles["content"]}>
          <div className={styles["selections-wrapper"]}>
            <Selections
              id={id}
              monthlyIncome={monthlyIncome}
              updateState={updateState}
              resetKey={resetKey}
              monthly={monthly}
              setState={setState}
              incomeIntegerLimit={incomeIntegerLimit}
              monthlyOptions={monthlyOptions}
              monthlyColors={monthlyColors}
              monthlyIntegerLimit={monthlyIntegerLimit}
              yearly={yearly}
              yearlyOptions={yearlyOptions}
              yearlyColors={yearlyColors}
              yearlyIntegerLimit={yearlyIntegerLimit}
              theme={theme}
            />
          </div>
          <div className={styles["graph"]}>
            <div
              aria-hidden="true"
              role="img"
              aria-label="Pie chart depicting budget categories and percent of total money spent in each category."
              className={styles["pie"]}
              data-testid="piechart"
            >
              <BudgetPieChart
                data={pieData}
                dataKey="cost"
                label="Budget"
                colors={pieColors}
                income={monthlyIncome}
                overspendColor={overspendColor}
                overspendTitle={overspendTitle}
              />
            </div>
            <callouts.Callout className="inline">
              <callouts.Detail className="inline-stat" result={util.formatDollarsNoCents(monthlyNetIncome)} label="Monthly Savings" dataTestId="monthly-savings" />
              <callouts.Detail className="inline-stat" result={util.formatDollarsNoCents(yearlyNetIncome)} label="Yearly Savings" dataTestId="yearly-savings" />
            </callouts.Callout>
          </div>
        </shared.Content>
        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    defaultProps: {
      id: "budget-calculator",
      className: "budget-calculator",
      title: "Budget Calculator",
      subtitle: "Insert your monthly income and your monthly and yearly expenses to find out your net monthly and yearly income.",
      underspendTitle: "Savings",
      underspendColor: "#DBDEE0",
      overspendTitle: "Loss",
      overspendColor: "#991125",
      colors: [
        "#FFBB00",
        "#64D254",
        "#C4A8DF",
        "#FF91B0",
        "#FF4444",
        "#6EC5EF",
        "#005F8B",
        "#00BDDC",
        "#65DFC5",
        "#9E6CC7",
        "#FF881F",
        "#FFE7AF",
        "#00637A",
        "#FFCBCF",
        "#006AF9",
        "#FFA971",
        "#EDFCAE",
        "#A7DD00",
        "#077259",
        "#A8AFDA"
      ],
      monthlyOptions: ["Rent / Mortgage", "Utilities", "Phone", "Internet", "Car Payment", "Car Insurance", "Car Repairs", "Gas", "Food", "Entertainment", "Clothing", "Other"],
      yearlyOptions: ["Life Insurance", "Property Taxes", "Medical", "Vacation", "Furniture", "Gifts", "Emergency", "Other"],
      incomeIntegerLimit: 6,
      monthlyIntegerLimit: 5,
      yearlyIntegerLimit: 6
    },
    stateFilter: (state) => {
      const {monthly, yearly} = state
      const {monthlyNetIncome, yearlyNetIncome} = calcThings(state)
      const _scrubIds = (list) => list.map(({name, cost}) => ({name, cost}))
      delete state.colorClaims
      return assign(state, {
        monthly: _scrubIds(monthly),
        yearly: _scrubIds(yearly),
        monthlyNetIncome,
        yearlyNetIncome
      })
    },
    defaultState: {
      colorClaims: inputs.newStickyIndex(),
      monthlyIncome: 3000,
      monthly: [
        {name: "Rent / Mortgage", cost: 600},
        {name: "Food", cost: 150},
        {name: "Entertainment", cost: 40}
      ],
      yearly: [
        {name: "Medical", cost: 3000},
        {name: "Gifts", cost: 500},
        {name: "Emergency", cost: 1000}
      ]
    }
  }
)
